/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint browser: true */
"use strict";

// Implements automatic browser window management.
// Features:
//
// 1. Focus an existing window instead of
//    opening a new one every time
// ---------
// If a document is already displayed in a child
// window, opening the same document should focus
// the existing window instead of opening a new one.
//
// How to use:
// a) Include "pli/forms" in your module
// b) Define method "pli.dialogId". It is expected
//    to return a string that will uniquely
//    identify document loaded in current browser
//    window, for example "documentType:documentId".
// c) Specify "options.id" when calling
//    Pli.dialog.open, for example:
//    Pli.dialog.open({ targetUrl: "~/Invoice/123", id: "invoice:123" })
//
//
// 2. Warn before closing a browser window
//    (typically about unsaved data)
// ---------
// If a document is edited in a child window,
// attempting to close this window should
// either auto-save it or warn user about
// unsaved changes. Simple dialog registry
// will not only show warning for the window,
// being closed, but warnings from all
// descendant windows as well. For example,
// if window A opened windows A1 and A2
// and A1 has unsaved changes (but not A or A2),
// attempting to close A will show warning
// from A1.
//
// How to use:
// a) Include "pli/forms" in your module
// b) Define method "pli.dialogClosingMessage".
//    It is expected to return a string
//    that describes why this browser window
//    can't be closed automatically.
//
//    Note that dialogClosingMessage function
//    will be called with one argument, "isRootLevel".
//    When true, current window initiated
//    close action. If false, an ancestor
//    window initiated close action.
//
//    Using this flag we can generate one
//    messge version if window is being closed
//    directly by user ("This invoice has
//    unsaved changes") and another version
//    if window is being closed by parent window,
//    e.g. if landing page is being closed
//    by user ("Invoice 123 has unsaved changes").
//
//
// 3. Automatically close all descendant windows
// ---------
// When a browser window closes, all descendant
// windows will be closed as well.
//
// How to use:
// a) Include "pli/forms" in your module

var $ = require("../jquery");
var pli = require("../core");
var dialog = require("./Dialog");


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Class "SimpleDialogRegistry"
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//---------------------------------------
function SimpleDialogRegistry() {
    /// <summary>
    /// Create new instance of simple dialog
    /// registry (singleton per browser window).
    /// Hijacks Pli.dialog.open and registers
    /// handlers for window.onBeforeUnload
    /// and window.onUnload events.
    /// </summary>

    this._isUnloading = false;
    this._children = []; // collection of child Pli objects

    // override Pli.dialog.open
    this._originalOpen = dialog.open;
    dialog.open = this._open.bind(this);

    // handle (current) window closing
    $(window)
        .on("beforeunload", this._beforeUnload.bind(this))
        .on("unload", this._unload.bind(this));
}


//---------------------------------------
SimpleDialogRegistry.prototype._beforeUnload = function registryBeforeUnload() {
    /// <summary>
    /// Handle window.onBeforeUnload event.
    /// Returns all warnings about unsaved
    /// data etc from this window and all
    /// its registered descendants. Returns
    /// undefined if no warnings are found
    /// (as per window.onBeforeUnload
    /// event expectations).
    /// </summary>

    if (this._isUnloading) { // parent already received user's approval to close
        return undefined;
    }

    var m = this._gatherMessages([], true);
    return (m.length
        ? m.join("\n\n")
        : undefined);
};


//---------------------------------------
SimpleDialogRegistry.prototype._findPli = function registryFindPli(id) {
    /// <summary>
    /// Find Pli object with the specified
    /// ID (recursively search all registered
    /// descendants). Returns null if no
    /// such Pli object can be found.
    /// </summary>

    if (pli.dialogId && pli.dialogId() === id) {
        return pli;
    }
    var i, l, p;
    for (i = 0, l = this._children.length; i < l; i += 1) {
        p = this._children[i].simpleDialogRegistry._findPli(id);
        if (p) { return p; }
    }
    return null;
};


//---------------------------------------
SimpleDialogRegistry.prototype._gatherMessages = function registryGatherMessages(result, isRootLevel) {
    /// <summary>
    /// Gather warnings about unsaved data etc
    /// from this window and all registered
    /// descendants.
    /// </summary>

    var i, l, m = pli.dialogClosingMessage && pli.dialogClosingMessage(isRootLevel);
    if (m) { result.push(m); }
    for (i = 0, l = this._children.length; i < l; i += 1) {
        this._children[i].simpleDialogRegistry._gatherMessages(result, false);
    }

    return result;
};


//---------------------------------------
SimpleDialogRegistry.prototype._open = function registryOpen(options, wnd) {
    /// <summary>
    /// This method replaces Pli.dialog.open.
    /// It first tries to locate an existing
    /// window with the requested ID (options.id).
    /// If this lookup fails, original Pli.dialog.open
    /// will be called to open a new window.
    /// If lookup succeeds, associated browser
    /// window is focused (unless prevented
    /// by browser security).
    /// </summary>

    if (options.id) {
        var foundPli = pli.rootPli.simpleDialogRegistry._findPli(options.id);
        if (foundPli) {
            foundPli.loaderWindow.focus();
            return foundPli.loaderWindow;
        }
    }

    // Requested ID was not found, open a new window
    return this._originalOpen(options, wnd);
};


//---------------------------------------
SimpleDialogRegistry.prototype._unload = function registryUnload() {
    /// <summary>
    /// Handle window.onUnload event.
    /// Unregisters from parent's simple dialog
    /// registry (if applicable) and force-closes
    /// all registered descendant browser windows.
    /// </summary>

    var i, l;
    if (!this._isUnloading && pli.parentPli) { // unregister from parent
        i = $.inArray(pli, pli.parentPli.simpleDialogRegistry._children);
        if (i >= 0) {
            pli.parentPli.simpleDialogRegistry._children.splice(i, 1);
        }
    }

    for (i = 0, l = this._children.length; i < l; i += 1) {
        this._children[i].simpleDialogRegistry._isUnloading = true; // prevent close confirmations a.k.a. force-close (this window or its parent already received user's approval)
        this._children[i].loaderWindow.close();
    }
};



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Initialization
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

// register this window with parent; will be unregistered
// in _unload method (window.onUnload event handler)
if (pli.parentPli) {
    pli.parentPli.simpleDialogRegistry._children.push(pli);
}

// create singleton for this browser window
// (becomes Pli.simpleDialogRegistry).
module.exports = new SimpleDialogRegistry();




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms/SimpleDialogRegistry.js
// module id = 390
// module chunks = 49